import React, { useEffect, useMemo, useState } from 'react';
import './FaqHelp.css';
import { apiRequest, parseResponse, getAuthToken } from './utils/api';
import { useLanguage } from './i18n/LanguageContext';
import { localeFor } from './i18n/translations';

const FILTERS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'This week' },
  { id: 'day', label: 'Day' },
  { id: 'month', label: 'Month' },
  { id: 'year', label: 'Year' }
];

const EMPTY_COPY = {
  today: 'No FAQs today',
  yesterday: 'No FAQs yesterday',
  week: 'No FAQs this week',
  day: 'No FAQs this day',
  month: 'No FAQs this month',
  year: 'No FAQs'
};

const STATUS_LABEL = {
  OPEN: 'Open',
  PROCESS: 'In progress',
  CLOSED: 'Closed'
};

const getUserId = () => {
  try {
    const guestData = JSON.parse(localStorage.getItem('guestData') || '{}');
    return guestData.userData?.data?.id || guestData.id || guestData.userId || null;
  } catch (error) {
    return null;
  }
};

const asUserId = (userId) => {
  if (typeof userId === 'number') return userId;
  const text = String(userId).trim();
  if (/^\d+$/.test(text)) return Number(text);
  return userId;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const startOfWeek = (date) => {
  const start = startOfDay(date);
  const weekday = start.getDay();
  const offset = weekday === 0 ? 6 : weekday - 1;
  start.setDate(start.getDate() - offset);
  return start;
};

const formatDayTitle = (date, locale) => date.toLocaleDateString(locale, {
  day: 'numeric',
  month: 'short',
  year: 'numeric'
});

const formatMonthTitle = (date, locale) => date.toLocaleDateString(locale, {
  month: 'long',
  year: 'numeric'
});

const formatWhen = (value, locale) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const statusLabel = (status) => STATUS_LABEL[String(status || '').toUpperCase()] || '';

const groupHistory = (items, filter, locale) => {
  const now = new Date();
  const sorted = [...items].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const filtered = sorted.filter((item) => {
    const date = new Date(item.createdAt);
    if (Number.isNaN(date.getTime())) return false;
    if (filter === 'today') return startOfDay(date).getTime() === startOfDay(now).getTime();
    if (filter === 'yesterday') {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return startOfDay(date).getTime() === startOfDay(yesterday).getTime();
    }
    if (filter === 'week') {
      const start = startOfWeek(now);
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      return date >= start && date < end;
    }
    return true;
  });

  const groups = new Map();
  filtered.forEach((item) => {
    const date = new Date(item.createdAt);
    let key = startOfDay(date).toISOString();
    let title = formatDayTitle(date, locale);
    if (filter === 'today') title = 'Today';
    if (filter === 'yesterday') title = 'Yesterday';
    if (filter === 'month') {
      key = `${date.getFullYear()}-${date.getMonth()}`;
      title = formatMonthTitle(date, locale);
    }
    if (filter === 'year') {
      key = String(date.getFullYear());
      title = key;
    }
    if (!groups.has(key)) groups.set(key, { key, title, items: [] });
    groups.get(key).items.push(item);
  });
  return [...groups.values()];
};

const FaqHelp = ({ onBack, initialAnswer = null }) => {
  const { t, language } = useLanguage();
  const locale = localeFor(language);
  const userId = getUserId();
  const [screen, setScreen] = useState(initialAnswer ? 'answer' : 'list');
  const [returnScreen, setReturnScreen] = useState('list');
  const [faqs, setFaqs] = useState([]);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState('');
  const [historyFilter, setHistoryFilter] = useState('day');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [listError, setListError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [answer, setAnswer] = useState(initialAnswer);
  const [loadingAnswer, setLoadingAnswer] = useState(false);
  const [answerError, setAnswerError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadFaqs = async () => {
      setLoadingList(true);
      setListError('');
      try {
        const response = await apiRequest('/faqs', { method: 'GET' });
        const parsed = await parseResponse(response);
        if (!response.ok || parsed?.status === false || !Array.isArray(parsed?.data)) {
          throw new Error(parsed?.message || 'Could not load FAQs.');
        }
        if (!cancelled) setFaqs(parsed.data);
      } catch (error) {
        if (!cancelled) setListError(error.message || 'Could not load FAQs.');
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    };
    loadFaqs();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  useEffect(() => {
    let cancelled = false;
    const loadHistory = async () => {
      if (!userId || !getAuthToken()) {
        setHistory([]);
        setLoadingHistory(false);
        return;
      }
      setLoadingHistory(true);
      setHistoryError('');
      try {
        const response = await apiRequest(`/faqs/history/user/${userId}`, { method: 'GET' });
        const parsed = await parseResponse(response);
        if (!response.ok || parsed?.status === false || !Array.isArray(parsed?.data)) {
          throw new Error(parsed?.message || 'Could not load FAQ history.');
        }
        if (!cancelled) {
          setHistory((current) => {
            const incoming = parsed.data;
            const incomingIds = new Set(incoming.map((item) => String(item.id)));
            const extras = current.filter((item) => item.id != null && !incomingIds.has(String(item.id)));
            return [...extras, ...incoming].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          });
        }
      } catch (error) {
        if (!cancelled) setHistoryError(error.message || 'Could not load FAQ history.');
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    };
    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [userId, reloadKey]);

  const visibleFaqs = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return faqs;
    return faqs.filter((item) => String(item.question || '').toLowerCase().includes(query));
  }, [faqs, search]);

  const historyGroups = useMemo(
    () => groupHistory(history, historyFilter, locale),
    [history, historyFilter, locale]
  );

  const openAnswer = (item, from) => {
    setReturnScreen(from);
    setAnswer(item);
    setAnswerError('');
    setLoadingAnswer(false);
    setScreen('answer');
  };

  const selectFaq = async (faq) => {
    setReturnScreen('list');
    setScreen('answer');
    setLoadingAnswer(true);
    setAnswerError('');
    setAnswer({ question: faq.question, answer: '' });
    const faqId = faq.id;

    const showFallback = async () => {
      const response = await apiRequest(`/faqs/${faqId}`, { method: 'GET' });
      const parsed = await parseResponse(response);
      const data = parsed?.data && typeof parsed.data === 'object' ? parsed.data : null;
      if (!response.ok || parsed?.status === false || !data) {
        throw new Error(parsed?.message || 'Could not load this answer.');
      }
      setAnswer({
        faqId: data.id || data.faqId || faqId,
        question: data.question || faq.question,
        answer: data.answer || ''
      });
    };

    try {
      if (!userId || !getAuthToken()) {
        await showFallback();
        return;
      }
      try {
        const response = await apiRequest(`/faqs/${faqId}/select`, {
          method: 'POST',
          body: JSON.stringify({ userId: asUserId(userId) })
        });
        const parsed = await parseResponse(response);
        const data = parsed?.data && typeof parsed.data === 'object' ? parsed.data : null;
        if (!response.ok || parsed?.status === false || !data) {
          throw new Error(parsed?.message || 'select failed');
        }
        if (data.chatId != null) {
          sessionStorage.setItem('faqHelpChatId', String(data.chatId));
        }
        const selected = {
          faqId: data.faqId || faqId,
          question: data.question || faq.question,
          answer: data.answer || '',
          historyId: data.historyId,
          chatId: data.chatId,
          chatStatus: data.chatStatus
        };
        setAnswer(selected);
        setHistory((current) => [{
          id: data.historyId || `local-${faqId}-${Date.now()}`,
          userId,
          faqId: selected.faqId,
          chatId: data.chatId,
          chatStatus: data.chatStatus,
          question: selected.question,
          answer: selected.answer,
          createdAt: new Date().toISOString()
        }, ...current]);
      } catch (selectError) {
        if (String(selectError?.message || '').includes('Token expired')) throw selectError;
        await showFallback();
      }
    } catch (error) {
      setAnswerError(error.message || 'Could not load this answer.');
    } finally {
      setLoadingAnswer(false);
    }
  };

  const badge = answer?.chatStatus ? t(statusLabel(answer.chatStatus)) : '';

  return (
    <div className="faq-help-page">
      <div className="faq-help-header">
        <button
          type="button"
          className="faq-back"
          aria-label={t('Go back')}
          onClick={() => {
            if (screen === 'answer' && !initialAnswer) {
              setScreen(returnScreen);
              return;
            }
            if (onBack) onBack();
          }}
        >
          <img src={`${process.env.PUBLIC_URL}/images/back_button.svg`} alt="" />
        </button>
        <div className="faq-heading">
          <h1 className="faq-title">{screen === 'answer' ? t('Answer') : t('FAQs')}</h1>
          {screen !== 'answer' && (
            <p className="faq-subtitle">{t('Questions and your previous help answers.')}</p>
          )}
        </div>
      </div>

      {screen !== 'answer' && (
        <div className="faq-switch" role="tablist" aria-label="FAQ sections">
          <button
            type="button"
            role="tab"
            aria-selected={screen === 'list'}
            className={screen === 'list' ? 'is-active' : ''}
            onClick={() => setScreen('list')}
          >
            {t('Questions')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={screen === 'history'}
            className={screen === 'history' ? 'is-active' : ''}
            onClick={() => setScreen('history')}
          >
            {t('History')}
          </button>
        </div>
      )}

      {screen === 'list' && (
        <div className="faq-panel">
          <label className="faq-search">
            <span className="visually-hidden">{t('Search questions')}</span>
            <input
              type="search"
              value={search}
              placeholder={t('Search questions')}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          {loadingList ? (
            <p className="faq-status">{t('Loading questions...')}</p>
          ) : listError ? (
            <div className="faq-status">
              <p>{listError}</p>
              <button type="button" onClick={() => setReloadKey((value) => value + 1)}>{t('Retry')}</button>
            </div>
          ) : visibleFaqs.length === 0 ? (
            <p className="faq-status">{search.trim() ? t('No matching questions.') : t('No FAQs yet.')}</p>
          ) : (
            <ul className="faq-list">
              {visibleFaqs.map((faq) => (
                <li key={faq.id}>
                  <button type="button" className="faq-question" onClick={() => selectFaq(faq)}>
                    <span>{faq.question}</span>
                    <span aria-hidden="true">›</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {screen === 'history' && (
        <div className="faq-panel">
          <div className="faq-filters" role="tablist" aria-label="History range">
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                role="tab"
                aria-selected={historyFilter === filter.id}
                className={historyFilter === filter.id ? 'is-active' : ''}
                onClick={() => setHistoryFilter(filter.id)}
              >
                {t(filter.label)}
              </button>
            ))}
          </div>
          {!userId || !getAuthToken() ? (
            <p className="faq-status">{t('Sign in to see your FAQ history.')}</p>
          ) : loadingHistory ? (
            <p className="faq-status">{t('Loading history...')}</p>
          ) : historyError ? (
            <div className="faq-status">
              <p>{historyError}</p>
              <button type="button" onClick={() => setReloadKey((value) => value + 1)}>{t('Retry')}</button>
            </div>
          ) : historyGroups.length === 0 ? (
            <p className="faq-status">{t(EMPTY_COPY[historyFilter])}</p>
          ) : (
            historyGroups.map((group) => (
              <section key={group.key} className="faq-group">
                <h2>{t(group.title)}</h2>
                <ul className="faq-list">
                  {group.items.map((item) => {
                    const label = t(statusLabel(item.chatStatus));
                    return (
                      <li key={item.id || `${item.faqId}-${item.createdAt}`}>
                        <button type="button" className="faq-history-item" onClick={() => openAnswer(item, 'history')}>
                          <span className="faq-history-top">
                            <span className="faq-history-question">{item.question}</span>
                            {label && (
                              <span className={`faq-badge is-${String(item.chatStatus).toLowerCase()}`}>{label}</span>
                            )}
                          </span>
                          <span className="faq-history-time">{formatWhen(item.createdAt, locale)}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </div>
      )}

      {screen === 'answer' && (
        <div className="faq-panel faq-answer">
          {loadingAnswer ? (
            <p className="faq-status">{t('Loading answer...')}</p>
          ) : answerError ? (
            <div className="faq-status">
              <p>{answerError}</p>
              <button type="button" onClick={() => setScreen(returnScreen)}>{t('Back to FAQs')}</button>
            </div>
          ) : (
            <>
              <div className="faq-answer-head">
                <h2>{answer?.question}</h2>
                {badge && (
                  <span className={`faq-badge is-${String(answer.chatStatus).toLowerCase()}`}>{badge}</span>
                )}
              </div>
              <p className="faq-answer-body">{answer?.answer || t('No answer is available for this question.')}</p>
              {!userId || !getAuthToken() ? <p className="faq-note">{t('Sign in to save this in your help history.')}</p> : null}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FaqHelp;
